import React from 'react';
import { useBundleProps } from 'signer-app/bundle-props';
import { useSignerAuth } from 'signer-app/signer-experience/auto-save-signer-experience/use-signer-auth';
import { useSignerReady } from 'signer-app/signer-experience/auto-save-signer-experience/use-signer-ready';
import { useFetchSignatureRequest } from 'signer-app/signer-experience/auto-save-signer-experience/use-fetch-signature-request';
import { useFieldAutoSave } from 'signer-app/signer-experience/auto-save-signer-experience/use-field-auto-save';
import { useUploadAttachment } from 'signer-app/signer-experience/auto-save-signer-experience/use-upload-attachment';
import { useEsignDisclosureOptIn } from 'signer-app/signer-experience/auto-save-signer-experience/use-esign-disclosure-opt-in';
import { useFinalize } from 'signer-app/signer-experience/auto-save-signer-experience/use-finalize';

export function useAutoSaveSignerExperience(signatureRequestId: string) {
  const { tsmGuid } = useBundleProps();

  const auth = useSignerAuth();

  const { readyData, readyIsFetching } = useSignerReady({
    tsmGuid,
    accessCode: auth.accessCode,
    setAuthState: auth.setAuthState,
    setSmsMeta: auth.setSmsMeta,
    setNumAttemptsLeft: auth.setNumAttemptsLeft,
  });

  const {
    data: fetchData,
    isFetching: fetchIsFetching,
    signatureRequest: fetchedSignatureRequest,
  } = useFetchSignatureRequest({
    signatureRequestId,
    accessCode: auth.accessCode,
    isReady: !!readyData?.ready,
  });

  const {
    fields,
    onFieldUpdate,
    flushDraft,
    isSavingRef,
    draftRef,
    backendError,
  } = useFieldAutoSave({
    signatureRequestId,
    accessCode: auth.accessCode,
    signatureRequest: fetchedSignatureRequest,
  });

  const { uploadAttachment } = useUploadAttachment({
    signatureRequestId,
    accessCode: auth.accessCode,
  });

  const { esignDisclosureOptIn } = useEsignDisclosureOptIn({
    signatureRequestId,
    accessCode: auth.accessCode,
  });

  const { finalize, isFinalizing, redirectUrl } = useFinalize({
    signatureRequestId,
    accessCode: auth.accessCode,
    tsmGuid,
    isSavingRef,
    draftRef,
  });

  const isFetching = readyIsFetching || fetchIsFetching;

  const data = React.useMemo(() => {
    if (auth.authState) {
      return auth.authState;
    }
    return fetchData;
  }, [auth.authState, fetchData]);

  const signatureRequest = React.useMemo(() => {
    if (!fetchedSignatureRequest) return undefined;
    return {
      ...fetchedSignatureRequest,
      fields,
    } as typeof fetchedSignatureRequest;
  }, [fetchedSignatureRequest, fields]);

  return {
    data,
    isFetching,
    signatureRequest,
    onFieldUpdate,
    flushDraft,
    uploadAttachment,
    esignDisclosureOptIn,
    finalize,
    isFinalizing,
    redirectUrl,
    backendError,
    accessCode: auth.accessCode,
    setAccessCode: auth.setAccessCode,
    smsMeta: auth.smsMeta,
    setSmsMeta: auth.setSmsMeta,
    setAuthState: auth.setAuthState,
    numAttemptsLeft: auth.numAttemptsLeft,
    clearAuth: auth.clearAuth,
  };
}
